import { Component, Input } from '@angular/core';
import type { OnChanges, OnDestroy, OnInit } from '@angular/core';
import { faBullseye, faCalendarAlt, faClipboardList, faClock, faCloudDownloadAlt, faFlag } from '@fortawesome/free-solid-svg-icons';
import { PageContainerService, TimerService } from '../../services';
import {
  ExecutionSummaryDto,
  ExecutionChartDataDto,
  ExecutionBarChartDto,
  ExecutionChartDataSeriesDto,
  ProjectSetupDto,
  GetP6ReportingLinkCommandRequest,
  GetP6ReportingLinkCommandResponse,
  SummaryStageDto
} from '../../data/CommandServiceDtos.generated';
import { MatDialog } from '@angular/material/dialog';
import { ProjectExecutionScurveComponent } from '../project-execution-scurve/project-execution-scurve.component';
import { ProjectAskDataComponent } from '../project-ask-data/project-ask-data.component';
import { clampPercent, formatPercent, gaugeStatus, GaugeStatus } from '../project-summary/summary-ui';
import { ProjectAccessArea } from '../../utilities/project-access-area';

@Component({
  standalone: false,
  selector: 'project-summary-execution',
  templateUrl: './project-summary-execution.component.html',
  styleUrls: ['./project-summary-execution.component.scss']
})
export class ProjectSummaryExecutionComponent implements OnInit, OnChanges, OnDestroy {
  private destroyed = false;

  @Input() executionSummary: ExecutionSummaryDto;
  @Input() chartData: ExecutionChartDataDto[];
  @Input() barChartData: ExecutionChartDataSeriesDto[];
  @Input() lineChartData: ExecutionBarChartDto;
  @Input() projectId: number;
  @Input() powerBiReportPath: string;
  @Input() projectDisplayType: string = '';
  @Input() project: ProjectSetupDto = new ProjectSetupDto();
  @Input() isActive = true;
  @Input() playToken = 0;

  iconExecutionLink = faClipboardList;
  iconTimeEntryLink = faClock;
  iconStatusReport = faCloudDownloadAlt;
  openingP6Reporting = false;
  faTarget = faBullseye;
  faCalendar = faCalendarAlt;
  faFlag = faFlag;
  faClock = faClock;

  executionOverallComplete: number;
  executionPlannedComplete: number = 0;
  executionProjectedPercent: number = 0;
  executionPlannedCompleteWidth: number = 0;
  executionProjectedPercentWidth: number = 0;
  executionAccessUrl: string;
  timeentryAccessUrl: string;
  projectStatusRptUrl: string;
  baselineTargetFinish: string;
  projectTargetFinish: string;
  projectDataDate: string;
  canShowTarReportButton = false;
  status: GaugeStatus = gaugeStatus(0);
  displayPercent = '0%';
  isPercentCompleteExpanded = true;
  isWbsExpanded = true;
  isTimelineExpanded = true;

  constructor(private pcs: PageContainerService, private dialog: MatDialog, private timerService: TimerService) {
  }

  togglePercentComplete(): void {
    this.isPercentCompleteExpanded = !this.isPercentCompleteExpanded;
  }

  toggleWbs(): void {
    this.isWbsExpanded = !this.isWbsExpanded;
  }

  toggleTimeline(): void {
    this.isTimelineExpanded = !this.isTimelineExpanded;
  }

  ngOnInit() {
    if (this.projectId === 189) {
      this.canShowTarReportButton = true;
    }
    this.setChartData();
  }

  ngOnChanges() {
    this.setChartData();
  }

  setChartData() {
    if (this.lineChartData !== undefined && this.lineChartData !== null) {
      this.executionOverallComplete = this.lineChartData.ActualPercentComplete;
      this.executionPlannedComplete = this.lineChartData.BaselinePercentComplete;
      this.executionProjectedPercent = this.lineChartData.ProjectedPercentComplete;
    } else {
      this.executionOverallComplete = this.executionSummary?.PercentComplete || 0;
      this.executionPlannedComplete = 0;
      this.executionProjectedPercent = 0;
    }

    this.executionPlannedCompleteWidth = this.executionPlannedComplete;
    this.executionProjectedPercentWidth = this.executionProjectedPercent;
    if (this.executionPlannedComplete > 100) {
      this.executionPlannedCompleteWidth = 100;
    }
    if (this.executionProjectedPercent > 100) {
      this.executionProjectedPercentWidth = 100;
    }

    this.executionAccessUrl = this.toHttps(this.executionSummary?.ExecutionAccessUrl);
    this.timeentryAccessUrl = this.toHttps(this.executionSummary?.TimeEntryAccessUrl);
    this.projectStatusRptUrl = this.toHttps(this.executionSummary?.ProjectStatusReportUrl);
    this.baselineTargetFinish = this.executionSummary?.BaselineFinishDate;
    this.projectTargetFinish = this.executionSummary?.ProjectFinishDate;
    this.projectDataDate = this.executionSummary?.ProjectDataDate;
    this.status = gaugeStatus(this.executionOverallComplete || 0);
    this.displayPercent = formatPercent(this.executionOverallComplete || 0, 1);
  }

  get showPercentRollups(): boolean {
    // Restore pre-revamp Actual/Planned/Projected bars when chart status data exists,
    // or when ProjectDisplayType explicitly includes horizontalBar.
    if (this.showChart('horizontalBar')) {
      return true;
    }
    return this.lineChartData !== undefined && this.lineChartData !== null;
  }

  get wbsLevels(): SummaryStageDto[] {
    return (this.executionSummary && this.executionSummary.ExecutionWbsLevels) || [];
  }

  formatPct(value: number): string {
    return formatPercent(value || 0, 2);
  }

  barWidth(value: number): number {
    return clampPercent(value || 0);
  }

  displayDate(value: string): string {
    if (!value || value === 'TBD') {
      return 'TBD';
    }
    return value;
  }

  private toHttps(url: string): string {
    if (!url) {
      return url;
    }
    return url.replace(/^http:\/\//i, 'https://');
  }

  drilldownExecution() {
    if (this.executionAccessUrl === null || this.executionAccessUrl.length === 0) {
      this.pcs.notificationService.warning('Project Schedule not configured for this Project', '', { timeOut: 3000 });
      return;
    }
    window.open(this.executionAccessUrl, '_blank');
  }

  launchTimeEntry() {
    if (this.timeentryAccessUrl === null || this.timeentryAccessUrl.length === 0) {
      this.pcs.notificationService.warning('Project Schedule not configured for this Project', '', { timeOut: 3000 });
      return;
    }
    window.open(this.timeentryAccessUrl, '_blank');
  }

  primaveraStatusReport() {
    if (this.primaveraStatusReport === null || this.projectStatusRptUrl.length === 0) {
      this.pcs.notificationService.warning('Project Status Report Not Available', 'Project Status Report not available for this Project', { timeOut: 3000 });
      return;
    }
    window.open(this.projectStatusRptUrl, '_blank');
  }

  /**
   * Hands the user off to the P6 Reporting dashboard, already signed in and scoped to this project.
   * The link carries a short-lived signed token minted server-side on every click, so nothing goes stale
   * while the summary page sits open. The blank tab is opened synchronously because browsers only allow
   * window.open() from inside the click handler itself — not after the await below.
   */
  async openP6Reporting() {
    if (this.openingP6Reporting) {
      return;
    }
    this.openingP6Reporting = true;
    const reportWindow = window.open('', '_blank');

    try {
      const response: GetP6ReportingLinkCommandResponse = await this.pcs.apiService.GetP6ReportingLink(
        { ProjectId: this.projectId } as GetP6ReportingLinkCommandRequest, true, false);

      if (!response || !response.Url) {
        if (reportWindow) { reportWindow.close(); }
        this.pcs.notificationService.warning('Detailed Reports is not available in this environment', '', { timeOut: 3000 });
        return;
      }

      if (reportWindow) {
        reportWindow.location.href = response.Url;
      } else {
        this.pcs.notificationService.warning('Please allow pop-ups for this site to open Detailed Reports', '', { timeOut: 5000 });
      }
    } catch (error) {
      if (reportWindow) { reportWindow.close(); }
      console.log('In ProjectSummaryExecutionComponent - openP6Reporting(), request failed.', error);
    } finally {
      this.openingP6Reporting = false;
    }
  }

  /**
   * Opens the "Ask the data" chat panel for this project. Questions are answered server-side by a
   * screened, read-only SQL query scoped to this project — see AskDataQuestionCommand.
   *
   * Time spent in here is execution time, not summary time, so the open access-log row is handed over
   * for as long as the dialog is up, exactly as openSCurve does.
   */
  openAskData() {
    this.handOverAccessArea(ProjectAccessArea.ProjectSummary, ProjectAccessArea.Execution);

    const dialogRef = this.dialog.open(ProjectAskDataComponent, {
      width: '860px',
      maxWidth: '96vw',
      height: '80vh',
      maxHeight: '90vh',
      data: {
        projectId: this.projectId,
        projectName: this.project && this.project.ProPakProject ? this.project.ProPakProject.Name : ''
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(() => {
      this.handOverAccessArea(ProjectAccessArea.Execution, ProjectAccessArea.ProjectSummary);
    });
  }

  chevronBlindFaithTARReport() {
    window.open('https://keystoneengineering.sharepoint.com/teams/solutions/ChevronProjectReadiness/SitePages/Blind_Faith_2022_TAR_Report.aspx', '_blank');
  }

  powerBiReport() {
    window.open(this.powerBiReportPath, '_blank');
  }

  openSCurve() {
    // The S-curve is the one execution view inside the app, so its time belongs
    // to Execution rather than to the summary page behind it. Hand the open
    // access log row over for as long as the dialog is up.
    this.handOverAccessArea(ProjectAccessArea.ProjectSummary, ProjectAccessArea.Execution);

    const dialogRef = this.dialog.open(ProjectExecutionScurveComponent, {
      width: '95vw',
      height: '95vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data: {
        chartData: this.chartData, projectId: this.projectId, executionSummary: this.executionSummary, project: this.project
      },
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(() => {
      // A route change closes the dialog too (closeOnNavigation). The summary
      // page is already unmounting in that case, so there is nothing to hand back.
      this.handOverAccessArea(ProjectAccessArea.Execution, this.destroyed ? null : ProjectAccessArea.ProjectSummary);
    });
  }

  private handOverAccessArea(closing: string, opening: string | null) {
    if (!this.projectId) {
      return;
    }

    // An idle time-out closes the dialog through the route change, so the
    // trailing idle window should come off this area too.
    this.pcs.apiService.LogTimeOut(
      { ProjectId: this.projectId, IsAutoRouted: this.timerService.wasIdleTriggered, TimeOut: this.timerService.lastActivity, AccessArea: closing } as any, false, false);

    if (opening) {
      this.pcs.apiService.LogTimeIn(
        { ProjectId: this.projectId, AccessArea: opening } as any, false, false);
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
  }

  showChart(type) {
    return this.projectDisplayType != undefined ? this.projectDisplayType.includes(type) : false;
  }
}
